import React, { useEffect, useRef, useState } from 'react';
import { ArrowDown, ArrowUp, Copy, GripVertical, Plus, QrCode as QrIcon, Sigma, Trash2, Upload } from 'lucide-react';
import { documentToolsApi } from '../../services/api';
import RichTextInput from './RichTextInput';
import { caretKey, useDesigner } from './context';
import { formatCell, previewFormula } from './formulaPreview';
import {
  DesignerDocument, DesignerNode, DesignerStyle, HEADING_SIZES, listMarkerLabel, PX_PER_MM, resolveStyle, SignatureParty, uid,
} from './model';
import { clampPerRow, DEFAULT_SIGN_HEIGHT, signatureRows } from './signatureBlock';

/** Absatzformat → CSS. Maße in pt/px wie in der PDF-Ausgabe. */
export function styleToCss(document: DesignerDocument, style?: DesignerStyle, extra?: DesignerStyle): React.CSSProperties {
  const resolved = { ...resolveStyle(document, style), ...(extra || {}) };
  const border = (spec?: { width?: number; color?: string; style?: string }) =>
    spec && spec.width ? `${spec.width}px ${spec.style || 'solid'} ${spec.color || '#111827'}` : undefined;
  return {
    textAlign: resolved.align,
    fontFamily: resolved.fontFamily === 'Times-Roman' ? 'Georgia, "Times New Roman", serif' : resolved.fontFamily === 'Courier' ? 'ui-monospace, Menlo, monospace' : 'Helvetica, Arial, sans-serif',
    fontSize: resolved.fontSize ? `${resolved.fontSize}pt` : undefined,
    fontWeight: resolved.bold ? 700 : undefined,
    fontStyle: resolved.italic ? 'italic' : undefined,
    textDecoration: [resolved.underline ? 'underline' : '', resolved.strike ? 'line-through' : ''].filter(Boolean).join(' ') || undefined,
    color: resolved.color,
    background: resolved.background,
    lineHeight: resolved.lineHeight,
    letterSpacing: resolved.letterSpacing ? `${resolved.letterSpacing}px` : undefined,
    textTransform: resolved.textTransform === 'none' ? undefined : resolved.textTransform,
    marginTop: resolved.marginTop,
    marginBottom: resolved.marginBottom,
    paddingLeft: (resolved.padding || 0) + (resolved.indentLeft || 0),
    paddingRight: (resolved.padding || 0) + (resolved.indentRight || 0),
    paddingTop: resolved.padding,
    paddingBottom: resolved.padding,
    textIndent: resolved.firstLineIndent,
    borderTop: border(resolved.borders?.top),
    borderRight: border(resolved.borders?.right),
    borderBottom: border(resolved.borders?.bottom),
    borderLeft: border(resolved.borders?.left),
    borderRadius: resolved.borderRadius,
  };
}

/** Ein Element im Seitenlayout inklusive Griff, Werkzeugen und Auswahlrahmen. */
export function NodeFrame({ node, children, nested }: { node: DesignerNode; children: React.ReactNode; nested?: boolean }) {
  const designer = useDesigner();
  const selected = designer.selectedId === node.id;
  return (
    <div
      data-node-id={node.id}
      onClick={event => { event.stopPropagation(); designer.select(node.id); }}
      onDragOver={event => { if (designer.dragged && designer.dragged !== node.id) event.preventDefault(); }}
      onDrop={event => { if (designer.dragged) { event.stopPropagation(); designer.reorder(node.id); } }}
      className={`group relative rounded ${selected ? 'outline outline-2 outline-primary-400' : 'hover:outline hover:outline-1 hover:outline-gray-200'}`}
      style={{ breakBefore: node.style?.pageBreakBefore ? 'page' : undefined, breakAfter: node.style?.pageBreakAfter ? 'page' : undefined }}
    >
      {!nested && (
        <div className="absolute -left-9 top-0 flex flex-col opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
          <button
            type="button"
            draggable
            onDragStart={event => { event.stopPropagation(); designer.setDragged(node.id); event.dataTransfer.effectAllowed = 'move'; }}
            onDragEnd={() => designer.setDragged(undefined)}
            aria-label="Element verschieben"
            className="p-1 text-gray-400 hover:text-gray-700 cursor-grab"
          >
            <GripVertical size={15} />
          </button>
        </div>
      )}
      <div className="absolute -right-1 -top-2 z-10 flex gap-0.5 bg-white border border-gray-200 rounded shadow-sm opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
        <button type="button" aria-label="Element nach oben" onClick={event => { event.stopPropagation(); designer.move(node.id, -1); }} className="p-1 text-gray-500 hover:text-primary-600"><ArrowUp size={13} /></button>
        <button type="button" aria-label="Element nach unten" onClick={event => { event.stopPropagation(); designer.move(node.id, 1); }} className="p-1 text-gray-500 hover:text-primary-600"><ArrowDown size={13} /></button>
        <button type="button" aria-label="Element duplizieren" onClick={event => { event.stopPropagation(); designer.duplicate(node.id); }} className="p-1 text-gray-500 hover:text-primary-600"><Copy size={13} /></button>
        <button type="button" aria-label="Element löschen" onClick={event => { event.stopPropagation(); designer.remove(node.id); }} className="p-1 text-gray-500 hover:text-red-600"><Trash2 size={13} /></button>
      </div>
      {children}
    </div>
  );
}

export function NodeView({ node, nested }: { node: DesignerNode; nested?: boolean }) {
  const designer = useDesigner();
  const css = styleToCss(designer.document, node.style);

  if (node.type === 'paragraph' || node.type === 'heading') return <TextNode node={node} css={css} />;
  if (node.type === 'list') return <ListNode node={node} css={css} />;
  if (node.type === 'container') return <ContainerNode node={node} css={css} />;
  if (node.type === 'table') return <TableNode node={node} css={css} />;
  if (node.type === 'image') return <ImageNode node={node} css={css} />;
  if (node.type === 'qr') return <QrNode node={node} css={css} />;
  if (node.type === 'signature') return <SignatureNode node={node} css={css} />;
  if (node.type === 'signatureBlock') return <SignatureBlockNode node={node} css={css} />;
  if (node.type === 'toc') return <TocNode node={node} css={css} />;
  if (node.type === 'divider') return <div style={{ ...css, borderTop: css.borderTop || '1px solid #d1d5db' }} aria-label="Trennlinie" />;
  if (node.type === 'spacer') return <div style={{ height: node.height || 24 }} className="bg-[repeating-linear-gradient(45deg,#f9fafb,#f9fafb_6px,#f3f4f6_6px,#f3f4f6_12px)] rounded" aria-label={`Abstandhalter ${node.height || 24} Punkt`} />;
  if (node.type === 'pageBreak') return (
    <div className="flex items-center gap-2 my-2 text-[11px] uppercase tracking-wide text-primary-600" aria-label="Seitenumbruch">
      <span className="flex-1 border-t border-dashed border-primary-300" />Seitenumbruch<span className="flex-1 border-t border-dashed border-primary-300" />
    </div>
  );
  return <div className="text-xs text-red-600">Unbekannter Elementtyp: {String((node as any).type)}</div>;
}

function TextNode({ node, css }: { node: DesignerNode; css: React.CSSProperties }) {
  const designer = useDesigner();
  const key = caretKey(node.id);
  const heading = node.type === 'heading';
  const level = node.level || 1;
  const number = designer.headingNumbers.get(node.id);
  return (
    <div className="flex gap-2 items-baseline" style={{ marginTop: css.marginTop, marginBottom: css.marginBottom }}>
      {heading && number && <span className="select-none text-gray-500 shrink-0" style={{ ...css, marginTop: 0, marginBottom: 0 }} aria-hidden="true">{number}</span>}
      <RichTextInput
        ariaLabel={heading ? `Überschrift Ebene ${level}` : 'Rich-Text-Absatz'}
        placeholder={heading ? 'Überschrift eingeben …' : 'Text eingeben …'}
        text={node.text || ''}
        marks={node.marks || []}
        readOnly={designer.readOnly}
        className="flex-1 min-h-[1.4em]"
        style={{ ...css, marginTop: 0, marginBottom: 0, fontSize: css.fontSize && !heading ? css.fontSize : `${node.style?.fontSize || HEADING_SIZES[level] || 16}pt`, fontWeight: heading ? 700 : css.fontWeight }}
        syncToken={designer.syncTokens[key] || 0}
        caret={designer.caret?.key === key ? designer.caret.range : null}
        autoFocus={designer.autoFocusKey === key}
        onChange={(text, marks) => designer.update(node.id, { text, marks })}
        onFocus={() => designer.select(node.id)}
        onSelectionChange={range => designer.onSelection(node.id, undefined, range)}
        onSplit={range => designer.onSplit(node.id, undefined, range)}
        onMergeBackward={() => designer.onMergeBackward(node.id)}
        onMergeForward={() => designer.onMergeForward(node.id)}
        onIndent={outdent => designer.onIndent(node.id, undefined, outdent)}
      />
    </div>
  );
}

function ListNode({ node, css }: { node: DesignerNode; css: React.CSSProperties }) {
  const designer = useDesigner();
  const items = node.items || [];
  return (
    <div style={{ marginTop: css.marginTop, marginBottom: css.marginBottom }} role="list" aria-label={node.ordered ? 'Nummerierte Liste' : 'Aufzählung'}>
      {items.map((item, index) => {
        const key = caretKey(node.id, item.id);
        return (
          <div key={item.id} role="listitem" className="flex gap-2 items-baseline" style={{ paddingLeft: (item.level || 0) * 22 }}>
            <span className="select-none text-gray-500 shrink-0 min-w-[1.4em]" style={{ fontSize: css.fontSize }} aria-hidden="true">{listMarkerLabel(node, index)}</span>
            <RichTextInput
              ariaLabel={`Listeneintrag ${index + 1}`}
              placeholder="Listeneintrag …"
              text={item.text || ''}
              marks={item.marks || []}
              readOnly={designer.readOnly}
              className="flex-1 min-h-[1.4em]"
              style={{ ...css, marginTop: 0, marginBottom: 0, paddingLeft: 0, paddingRight: 0 }}
              syncToken={designer.syncTokens[key] || 0}
              caret={designer.caret?.key === key ? designer.caret.range : null}
              autoFocus={designer.autoFocusKey === key}
              onChange={(text, marks) => designer.update(node.id, { items: items.map(entry => (entry.id === item.id ? { ...entry, text, marks } : entry)) })}
              onFocus={() => designer.select(node.id, item.id)}
              onSelectionChange={range => designer.onSelection(node.id, item.id, range)}
              onSplit={range => designer.onSplit(node.id, item.id, range)}
              onMergeBackward={() => designer.onMergeBackward(node.id, item.id)}
              onMergeForward={() => designer.onMergeForward(node.id, item.id)}
              onIndent={outdent => designer.onIndent(node.id, item.id, outdent)}
            />
          </div>
        );
      })}
    </div>
  );
}

function ContainerNode({ node, css }: { node: DesignerNode; css: React.CSSProperties }) {
  const designer = useDesigner();
  const columns = node.columns || [];
  const gap = node.style?.columnGap ?? 12;
  return (
    <div style={{ ...css, display: 'flex', gap, alignItems: node.style?.verticalAlign === 'middle' ? 'center' : node.style?.verticalAlign === 'bottom' ? 'flex-end' : 'flex-start' }}>
      {columns.map((column: any) => (
        <div
          key={column.id}
          style={{ width: `${column.width}%` }}
          className="min-h-[2rem] border border-dashed border-gray-200 rounded p-1 space-y-1"
          onDragOver={event => { if (designer.dragged) event.preventDefault(); }}
          onDrop={event => { event.stopPropagation(); designer.moveInto(node.id, column.id); }}
        >
          {(column.nodes || []).map((child: DesignerNode) => (
            <NodeFrame key={child.id} node={child} nested>
              <NodeView node={child} nested />
            </NodeFrame>
          ))}
          {!(column.nodes || []).length && <p className="text-[11px] text-gray-400 p-2">Element hierher ziehen</p>}
        </div>
      ))}
    </div>
  );
}

function ImageNode({ node, css }: { node: DesignerNode; css: React.CSSProperties }) {
  const designer = useDesigner();
  const input = useRef<HTMLInputElement>(null);
  const pick = (file?: File | null) => {
    if (!file) return;
    if (file.size > 4_000_000) { window.alert('Das Bild darf höchstens 4 MB groß sein.'); return; }
    const reader = new FileReader();
    reader.onload = () => designer.update(node.id, { src: String(reader.result), alt: node.alt || file.name });
    reader.readAsDataURL(file);
  };
  return (
    <figure style={{ ...css, textAlign: node.style?.align || 'left' }} className="m-0">
      {node.src ? (
        <img src={node.src} alt={node.alt || 'Bild'} style={{ width: node.width ? `${node.width}px` : undefined, height: node.height ? `${node.height}px` : undefined, maxWidth: '100%' }} className="inline-block" />
      ) : (
        <button type="button" onClick={() => input.current?.click()} className="w-full border-2 border-dashed border-gray-300 rounded p-8 text-sm text-gray-500 hover:border-primary-400">
          <Upload className="mx-auto mb-2" size={20} />Bild auswählen (PNG, JPG, max. 4 MB)
        </button>
      )}
      <input ref={input} type="file" accept="image/png,image/jpeg,image/gif,image/webp" aria-label="Bilddatei wählen" className="hidden" onChange={event => pick(event.target.files?.[0])} />
      {node.caption && <figcaption className="text-xs text-gray-500 mt-1">{node.caption}</figcaption>}
    </figure>
  );
}

function QrNode({ node, css }: { node: DesignerNode; css: React.CSSProperties }) {
  const [dataUrl, setDataUrl] = useState('');
  const [error, setError] = useState('');
  useEffect(() => {
    let active = true;
    const timer = setTimeout(() => {
      documentToolsApi.previewQr(node.payload)
        .then(result => { if (active) { setDataUrl(result.dataUrl); setError(''); } })
        .catch(err => { if (active) { setDataUrl(''); setError(err.response?.data?.error || 'QR-Code ungueltig'); } });
    }, 250);
    return () => { active = false; clearTimeout(timer); };
  }, [node.payload]);
  const size = node.size || 90;
  return (
    <div style={{ ...css, textAlign: node.style?.align || 'left' }} className="py-2">
      {dataUrl
        ? <img src={dataUrl} alt={`QR-Code ${node.payload?.type}`} style={{ width: size, height: size }} className="inline-block" />
        : <QrIcon className="inline-block" size={size} />}
      <div className={`text-xs mt-1 ${error ? 'text-red-600' : 'text-gray-500'}`}>{error || `${node.payload?.type}: ${node.payload?.url || node.payload?.iban || node.payload?.lastName || ''}`}</div>
      {node.caption && <div className="text-xs text-gray-500">{node.caption}</div>}
    </div>
  );
}

function SignatureNode({ node, css }: { node: DesignerNode; css: React.CSSProperties }) {
  const provider = { LOCAL: 'Handschriftlich', SKRIBBLE: 'Skribble', ATRUST: 'ID Austria' }[node.provider || 'LOCAL'];
  return (
    <div style={css} className="pt-10">
      <div className="border-b-2 border-gray-800 h-10" />
      <div className="text-xs text-gray-700 mt-1">{node.label || 'Unterschrift'}{node.role ? ` · ${node.role}` : ''}</div>
      <div className="text-[11px] text-gray-400">{provider}{node.showPlace ? ' · Ort' : ''}{node.showDate ? ' · Datum' : ''}</div>
    </div>
  );
}

/**
 * Unterschriftenblock: Tabelle mit standardmäßig zwei Unterschriften je Zeile.
 * Die Namen der Vertragsparteien werden direkt unter der jeweiligen Linie bearbeitet.
 */
function SignatureBlockNode({ node, css }: { node: DesignerNode; css: React.CSSProperties }) {
  const designer = useDesigner();
  const parties: SignatureParty[] = node.parties || [];
  const perRow = clampPerRow(node.perRow);
  const signHeight = node.signHeight ?? DEFAULT_SIGN_HEIGHT;
  const rows = signatureRows(parties, perRow);
  const rename = (id: string, name: string) =>
    designer.update(node.id, { parties: parties.map(party => (party.id === id ? { ...party, name } : party)) });

  return (
    <div style={css}>
      <table className="w-full table-fixed border-collapse" aria-label="Unterschriftenblock">
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {Array.from({ length: perRow }, (_unused, columnIndex) => {
                const party = row[columnIndex];
                const position = rowIndex * perRow + columnIndex;
                return (
                  <td
                    key={party?.id || `leer-${columnIndex}`}
                    className="align-top"
                    style={{ paddingRight: columnIndex < perRow - 1 ? 16 : 0, paddingBottom: rowIndex < rows.length - 1 ? signHeight / 2 + 8 : 0 }}
                  >
                    {party && (
                      <>
                        <div style={{ height: signHeight }} className="border-b border-gray-800" />
                        <input
                          aria-label={`Name der ${position + 1}. Vertragspartei`}
                          placeholder="Name der Vertragspartei"
                          readOnly={designer.readOnly}
                          className="w-full mt-1 bg-transparent text-center outline-none border-b border-dashed border-transparent hover:border-gray-300 focus:border-primary-400"
                          value={party.name || ''}
                          onChange={event => rename(party.id, event.target.value)}
                        />
                      </>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      {!parties.length && <p className="text-xs text-gray-400">Noch keine Vertragsparteien – im Eigenschaftenbereich ergänzen.</p>}
    </div>
  );
}

function TocNode({ node, css }: { node: DesignerNode; css: React.CSSProperties }) {
  const designer = useDesigner();
  const maxLevel = node.maxLevel || 3;
  const headings: DesignerNode[] = [];
  const walk = (nodes: DesignerNode[]) => nodes.forEach(entry => {
    if (entry.type === 'heading' && (entry.level || 1) <= maxLevel) headings.push(entry);
    if (entry.type === 'container') (entry.columns || []).forEach((column: any) => walk(column.nodes || []));
  });
  walk(designer.document.nodes);
  return (
    <nav style={css} aria-label="Inhaltsverzeichnis">
      <div className="font-semibold mb-1">{node.title || 'Inhaltsverzeichnis'}</div>
      {headings.length === 0 && <p className="text-xs text-gray-400">Noch keine Überschriften im Dokument.</p>}
      {headings.map(heading => (
        <button
          key={heading.id}
          type="button"
          onClick={() => designer.select(heading.id)}
          className="w-full text-left flex gap-2 hover:text-primary-600"
          style={{ paddingLeft: ((heading.level || 1) - 1) * 14 }}
        >
          <span className="text-gray-500">{designer.headingNumbers.get(heading.id)}</span>
          <span className="flex-1 truncate">{heading.text || 'Überschrift'}</span>
        </button>
      ))}
    </nav>
  );
}

// ==================== Tabelle ====================

function TableNode({ node, css }: { node: DesignerNode; css: React.CSSProperties }) {
  const designer = useDesigner();
  const [formulaTarget, setFormulaTarget] = useState<{ rowId: string; key: string } | null>(null);
  const columns: any[] = node.columns || [];
  const rows: any[] = node.rows || [];
  const cellMap: Record<string, string | number | null> = {};
  rows.forEach((row, rowIndex) => columns.forEach(column => { cellMap[`${column.key.toUpperCase()}${rowIndex + 1}`] = row.cells?.[column.key] ?? null; }));

  const setCell = (rowId: string, key: string, value: string) =>
    designer.update(node.id, { rows: rows.map(row => (row.id === rowId ? { ...row, cells: { ...row.cells, [key]: value } } : row)) });
  const addRow = (at = rows.length) => {
    const next = [...rows];
    next.splice(at, 0, { id: uid(), cells: Object.fromEntries(columns.map(column => [column.key, ''])) });
    designer.update(node.id, { rows: next });
  };
  const addColumn = () => {
    const used = new Set(columns.map(column => column.key.toUpperCase()));
    let key = 'A';
    for (let index = 0; index < 26; index++) { const candidate = String.fromCharCode(65 + index); if (!used.has(candidate)) { key = candidate; break; } }
    const width = Math.floor(100 / (columns.length + 1));
    designer.update(node.id, {
      columns: [...columns.map(column => ({ ...column, width })), { id: uid(), key, label: `Spalte ${key}`, valueType: 'NUMBER', width, align: 'right' }],
    });
  };
  const removeColumn = (id: string) => {
    const column = columns.find(entry => entry.id === id);
    if (!column || columns.length <= 1) return;
    designer.update(node.id, {
      columns: columns.filter(entry => entry.id !== id),
      rows: rows.map(row => { const cells = { ...row.cells }; delete cells[column.key]; return { ...row, cells }; }),
    });
  };

  return (
    <div style={css}>
      <table className="w-full border-collapse text-sm" style={{ border: `1px solid ${node.borderColor || '#9ca3af'}` }}>
        <thead>
          <tr className={node.headerRow === false ? 'hidden' : 'bg-gray-100'}>
            {columns.map(column => (
              <th key={column.id} className="border p-1" style={{ width: column.width ? `${column.width}%` : undefined, borderColor: node.borderColor || '#9ca3af', textAlign: column.align || 'left' }}>
                <div className="flex items-center gap-1">
                  <input
                    aria-label={`Spalte ${column.key}`}
                    className="w-full font-semibold bg-transparent outline-none"
                    value={column.label}
                    onChange={event => designer.update(node.id, { columns: columns.map(entry => (entry.id === column.id ? { ...entry, label: event.target.value } : entry)) })}
                  />
                  <button type="button" aria-label={`Spalte ${column.key} löschen`} className="text-gray-400 hover:text-red-600" onClick={() => removeColumn(column.id)}><Trash2 size={12} /></button>
                </div>
              </th>
            ))}
            <th className="w-8" />
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr key={row.id} className={node.zebra && rowIndex % 2 === 1 ? 'bg-gray-50' : undefined}>
              {columns.map(column => {
                const raw = row.cells?.[column.key] ?? '';
                const isFormula = String(raw).trim().startsWith('=');
                const computed = isFormula ? previewFormula(String(raw), cellMap) : null;
                return (
                  <td key={column.id} className="border p-0.5 align-top" style={{ borderColor: node.borderColor || '#d1d5db' }}>
                    <div className="flex items-center gap-1">
                      <input
                        aria-label={`${column.key}${rowIndex + 1}`}
                        className={`w-full p-1 bg-transparent outline-none ${isFormula ? 'font-mono text-primary-700' : ''}`}
                        style={{ textAlign: column.align || 'left' }}
                        value={String(raw)}
                        onChange={event => setCell(row.id, column.key, event.target.value)}
                      />
                      <button type="button" title="Formel visuell bearbeiten" aria-label={`Formel ${column.key}${rowIndex + 1} bearbeiten`} onClick={() => setFormulaTarget({ rowId: row.id, key: column.key })} className="text-primary-600 text-xs px-1">fx</button>
                    </div>
                    {isFormula && (
                      <div className="px-1 pb-0.5 text-[11px] text-gray-500" aria-label={`Vorschau ${column.key}${rowIndex + 1}`}>
                        {computed === null ? 'nicht berechenbar' : `= ${formatCell(computed, column.valueType, column.decimals)}`}
                      </div>
                    )}
                  </td>
                );
              })}
              <td className="text-center">
                <button type="button" aria-label={`Zeile ${rowIndex + 1} löschen`} className="text-gray-400 hover:text-red-600" onClick={() => designer.update(node.id, { rows: rows.filter(entry => entry.id !== row.id) })}><Trash2 size={13} /></button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex flex-wrap gap-2 mt-2">
        <button type="button" className="text-xs border rounded px-2 py-1 hover:bg-gray-50" onClick={() => addRow()}><Plus size={12} className="inline" /> Zeile</button>
        <button type="button" className="text-xs border rounded px-2 py-1 hover:bg-gray-50" onClick={addColumn}><Plus size={12} className="inline" /> Spalte</button>
        <button
          type="button"
          className="text-xs border rounded px-2 py-1 hover:bg-gray-50"
          onClick={() => {
            const last = columns[columns.length - 1];
            if (!last) return;
            designer.update(node.id, { rows: [...rows, { id: uid(), cells: { [columns[0].key]: 'Summe', [last.key]: `=SUM(${last.key}1:${last.key}${rows.length})` } }] });
          }}
        >
          <Sigma size={12} className="inline" /> Spaltensumme
        </button>
      </div>
      {formulaTarget && (
        <CellFormulaBuilder
          node={node}
          target={formulaTarget}
          cells={cellMap}
          close={() => setFormulaTarget(null)}
          save={formula => { setCell(formulaTarget.rowId, formulaTarget.key, formula); setFormulaTarget(null); }}
        />
      )}
    </div>
  );
}

function CellFormulaBuilder({ node, target, cells, save, close }: {
  node: DesignerNode; target: { rowId: string; key: string }; cells: Record<string, string | number | null>; save: (formula: string) => void; close: () => void;
}) {
  const rows: any[] = node.rows || [];
  const columns: any[] = node.columns || [];
  const row = rows.find(entry => entry.id === target.rowId);
  const initial = String(row?.cells?.[target.key] || '=');
  const [formula, setFormula] = useState(initial.startsWith('=') ? initial : `=${initial}`);
  const references = rows.flatMap((_row, rowIndex) => columns.map(column => `${column.key}${rowIndex + 1}`));
  const preview = previewFormula(formula, cells);
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="Visueller Formeleditor">
      <div className="bg-white rounded-xl shadow-xl p-5 w-full max-w-xl">
        <h3 className="font-semibold mb-3">Formel für {target.key}{rows.findIndex(entry => entry.id === target.rowId) + 1}</h3>
        <input autoFocus className="w-full border rounded p-3 font-mono" aria-label="Formel" value={formula} onChange={event => setFormula(event.target.value)} />
        <div className="mt-2 text-xs" aria-label="Formelvorschau">{preview === null ? <span className="text-amber-600">Noch nicht berechenbar</span> : <span className="text-green-700">Ergebnis: {preview}</span>}</div>
        <div className="flex flex-wrap gap-1 my-3">
          {['+', '-', '*', '/', '(', ')', 'SUM(', 'AVERAGE(', 'MIN(', 'MAX(', 'COUNT(', ':'].map(token => (
            <button key={token} type="button" className="border rounded px-2 py-1 font-mono text-sm hover:bg-gray-50" onClick={() => setFormula(value => value + token)}>{token}</button>
          ))}
        </div>
        <p className="text-xs text-gray-500 mb-1">Zellen anklicken:</p>
        <div className="flex flex-wrap gap-1 max-h-28 overflow-auto">
          {references.map(reference => (
            <button key={reference} type="button" className="bg-primary-50 text-primary-700 rounded px-2 py-1 font-mono text-xs" onClick={() => setFormula(value => value + reference)}>{reference}</button>
          ))}
        </div>
        <div className="mt-3 p-2 bg-gray-50 text-xs rounded">Beispiele: <code>=B1+B2</code>, <code>=SUM(B1:B5)</code>, <code>=SUM(B)</code>, <code>=AVERAGE(B1:B5)</code></div>
        <div className="flex justify-end gap-2 mt-4">
          <button type="button" onClick={close} className="border rounded px-4 py-2">Abbrechen</button>
          <button type="button" onClick={() => save(formula)} disabled={!/^=[A-Z0-9_+\-*/():.,\s]+$/i.test(formula)} className="bg-primary-600 text-white rounded px-4 py-2 disabled:opacity-40">Übernehmen</button>
        </div>
      </div>
    </div>
  );
}

export const millimetersToPixels = (millimeters: number) => millimeters * PX_PER_MM;
